import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronLeftIcon, PlusIcon, TrashIcon } from "@heroicons/react/24/outline";
import { api, ApiError } from "../lib/api";
import { useToast } from "../components/Toast";

/**
 * Einstellungen › Karten (Beleg-Eingang Teil 2, 2026-10-17).
 * Eine Karte trägt die Zuordnung (Verwendungsart) EINMAL; darunter alle Endziffern, die auf Bons erscheinen können
 * (Plastikkarte, Apple Pay auf iPhone/Watch = eigene Gerätekontonummer: Wallet → Karte → ⓘ → Karteninformationen).
 * Die Karte BESTÄTIGT nur eine Zuordnung, sie entscheidet nie allein (eine Privatkarte kann Geschäftliches bezahlen).
 */
type Nummer = { id: string; endziffern: string; art: string };
type Karte = { id: string; bezeichnung: string; steuerkategorie: string | null; aktiv: boolean; nummern: Nummer[] };

const ARTEN: Record<string, string> = { karte: "Karte", iphone: "Apple Pay iPhone", watch: "Apple Pay Watch", sonstiges: "Sonstiges" };

function fehlertext(e: unknown) {
  return e instanceof ApiError ? e.message : "Speichern fehlgeschlagen";
}

export function KartenPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const { data, isLoading } = useQuery<Karte[]>({ queryKey: ["karten"], queryFn: () => api.get("/api/karten") });
  const neu = () => void qc.invalidateQueries({ queryKey: ["karten"] });
  const anlegen = useMutation({
    mutationFn: (v: { bezeichnung: string }) => api.post("/api/karten", v),
    onSuccess: neu, onError: (e) => toast.error(fehlertext(e)),
  });
  const [bez, setBez] = useState("");

  return (
    <div className="max-w-2xl mx-auto p-4 lg:p-6">
      <Link to="/einstellungen" className="inline-flex items-center gap-1 text-ios-blue text-[14px] mb-3">
        <ChevronLeftIcon className="w-4 h-4" /> Einstellungen
      </Link>
      <h1 className="text-[22px] font-semibold tracking-tight mb-1">Karten</h1>
      <p className="text-[13px] text-ios-muted mb-4">
        Endziffern, die auf Kassenbons stehen können. Später helfen sie, einen Beleg einer Steuerkategorie zuzuordnen — als
        Bestätigung, nie als alleinige Entscheidung (die Zuordnung je Karte kommt mit den Steuerkategorien). Für Apple Pay die Gerätekontonummer eintragen (Wallet → Karte → ⓘ → Karteninformationen),
        je Gerät eine.
      </p>

      <form
        className="bg-white rounded-iosLg shadow-card p-3 mb-4 flex gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          if (!bez.trim()) return;
          anlegen.mutate({ bezeichnung: bez.trim() }, { onSuccess: () => setBez("") });
        }}
      >
        <input
          value={bez} onChange={(e) => setBez(e.target.value)} placeholder="Neue Karte, z. B. „Visa privat“"
          className="flex-1 border border-ios-line rounded-lg px-3 py-2 text-[16px] lg:text-[14px]" maxLength={60}
        />
        <button type="submit" className="inline-flex items-center gap-1 bg-ios-blue text-white rounded-lg px-3 text-[14px]">
          <PlusIcon className="w-4 h-4" /> Anlegen
        </button>
      </form>

      {isLoading && <div className="text-[14px] text-ios-muted">Lädt …</div>}
      {data?.length === 0 && <div className="text-[14px] text-ios-muted">Noch keine Karte eingetragen.</div>}
      <div className="space-y-3">
        {data?.map((k) => <KarteKarte key={k.id} k={k} />)}
      </div>
    </div>
  );
}

function KarteKarte({ k }: { k: Karte }) {
  const qc = useQueryClient();
  const toast = useToast();
  const neu = () => void qc.invalidateQueries({ queryKey: ["karten"] });
  const aendern = useMutation({
    mutationFn: (v: Partial<{ bezeichnung: string; aktiv: boolean }>) => api.patch(`/api/karten/${k.id}`, v),
    onSuccess: neu, onError: (e) => toast.error(fehlertext(e)),
  });
  const loeschen = useMutation({
    mutationFn: () => api.del(`/api/karten/${k.id}`), onSuccess: neu, onError: (e) => toast.error(fehlertext(e)),
  });
  const nummerNeu = useMutation({
    mutationFn: (v: { endziffern: string; art: string }) => api.post(`/api/karten/${k.id}/nummern`, v),
    onSuccess: neu, onError: (e) => toast.error(fehlertext(e)),
  });
  const nummerWeg = useMutation({
    mutationFn: (id: string) => api.del(`/api/karten-nummern/${id}`), onSuccess: neu, onError: (e) => toast.error(fehlertext(e)),
  });
  const [ziffern, setZiffern] = useState("");
  const [art, setArt] = useState("karte");

  return (
    <div className={`bg-white rounded-iosLg shadow-card p-4 ${k.aktiv ? "" : "opacity-60"}`}>
      <div className="flex items-center gap-2 mb-3">
        <div className="flex-1 font-semibold text-[16px]">{k.bezeichnung}</div>
        <label className="text-[13px] text-ios-muted flex items-center gap-1">
          <input type="checkbox" checked={k.aktiv} onChange={(e) => aendern.mutate({ aktiv: e.target.checked })} /> aktiv
        </label>
        <button
          type="button" title="Karte löschen" className="text-ios-red p-1"
          onClick={() => { if (window.confirm(`Karte „${k.bezeichnung}“ mit allen Endziffern löschen?`)) loeschen.mutate(); }}
        >
          <TrashIcon className="w-5 h-5" />
        </button>
      </div>

      <div className="text-[13px] text-ios-muted mb-1">Endziffern</div>
      {k.nummern.length === 0 && <div className="text-[13px] text-ios-muted mb-2">Noch keine.</div>}
      <ul className="mb-2">
        {k.nummern.map((n) => (
          <li key={n.id} className="flex items-center gap-2 py-1 border-b border-ios-line last:border-0">
            <span className="font-mono text-[15px]">•••• {n.endziffern}</span>
            <span className="text-[13px] text-ios-muted flex-1">{ARTEN[n.art] ?? n.art}</span>
            <button type="button" title="Endziffern entfernen" className="text-ios-red p-1" onClick={() => nummerWeg.mutate(n.id)}>
              <TrashIcon className="w-4 h-4" />
            </button>
          </li>
        ))}
      </ul>
      <form
        className="flex gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          // Nie kürzen: eine eingefügte volle Nummer ergäbe sonst still die ERSTEN 4 Ziffern (falsch).
          if (ziffern.length > 4) { toast.warn("Bitte nur die letzten 4 Ziffern eintragen"); return; }
          if (ziffern.length < 4) { toast.warn("Bitte genau 4 Ziffern eingeben"); return; }
          nummerNeu.mutate({ endziffern: ziffern, art }, { onSuccess: () => setZiffern("") });
        }}
      >
        <input
          value={ziffern} onChange={(e) => setZiffern(e.target.value.replace(/\D/g, ""))}
          inputMode="numeric" autoComplete="off" placeholder="4 Ziffern" aria-label="Endziffern"
          className="w-28 border border-ios-line rounded-lg px-3 py-2 font-mono text-[16px] lg:text-[14px]"
        />
        <select value={art} onChange={(e) => setArt(e.target.value)} className="flex-1 border border-ios-line rounded-lg px-2 text-[16px] lg:text-[14px] bg-white">
          {Object.entries(ARTEN).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <button type="submit" className="inline-flex items-center gap-1 bg-ios-blue text-white rounded-lg px-3 text-[14px]">
          <PlusIcon className="w-4 h-4" /> Hinzufügen
        </button>
      </form>
    </div>
  );
}
